/**
 * Kafka topic construction for websocket signal filters (LT-3186).
 *
 * Every live-data subscription in the console is a `kafkaTopic` string built from an
 * id — `OnPrem.EquipmentData_<equipmentId>`, `OnPrem.MLPredict_<cellId>`, and so on.
 * Building those by template interpolation is the hazard this module exists to remove:
 * a nullish id does not throw, it silently produces a well-formed-looking but invalid
 * topic (`OnPrem.EquipmentData_undefined`). Since LT-3062 the ws server validates
 * topics, so the result is a `Rejected N invalid topic filter(s)` warn server-side plus
 * a chart that is quietly missing its live feed, with nothing in the browser console to
 * explain it.
 *
 * LT-3114 fixed the one site that concatenated a null `kafkaTopic` into `"OnPrem.null"`.
 * This module is the general form: build topics through `buildKafkaTopic` (returns null
 * rather than interpolating a bad id) and let `sanitizeTopicFilters` backstop the sink,
 * so a future call site cannot reintroduce the bug.
 */

/**
 * True when `id` is usable as the id segment of a Kafka topic.
 *
 * Rejects 0 as well as nullish and non-finite values: 0 is not a real equipment/cell
 * primary key, and every hand-rolled guard this module replaces was a truthiness check
 * (`if (!resolvedEquipmentId) return`, `.filter(Boolean)`, `.filter((t) => t.equipmentId)`),
 * so treating 0 as invalid keeps the behaviour identical at those sites.
 */
export const isValidTopicId = (id?: number | null): id is number =>
    typeof id === 'number' && Number.isFinite(id) && id > 0;

/**
 * Build `<prefix><base>_<id>`, or return null when `id` is not a valid topic id.
 *
 * `prefix` is the facility topic prefix — `"OnPrem."` for on-prem facilities, `""` for
 * cloud-only ones (which is why a bad id on a cloud-only facility yields the bare string
 * `"undefined"`, not `"OnPrem.undefined"`).
 *
 * Returning null instead of throwing is deliberate: a missing id means "no live topic to
 * route to", which is a legitimate historical-only state for a tag, not an error.
 */
export const buildKafkaTopic = (
    prefix: string,
    base: string,
    id?: number | null,
): string | null => (isValidTopicId(id) ? `${prefix}${base}_${id}` : null);

/** `<prefix>EquipmentData_<equipmentId>`, or null for a nullish/invalid equipment id. */
export const buildEquipmentTopic = (prefix: string, equipmentId?: number | null): string | null =>
    buildKafkaTopic(prefix, 'EquipmentData', equipmentId);

/** `<prefix>MLPredict_<cellId>`, or null for a nullish/invalid cell id. */
export const buildMlPredictTopic = (prefix: string, cellId?: number | null): string | null =>
    buildKafkaTopic(prefix, 'MLPredict', cellId);

/** `<prefix>CellAlerts_<cellId>`, or null for a nullish/invalid cell id. */
export const buildCellAlertsTopic = (prefix: string, cellId?: number | null): string | null =>
    buildKafkaTopic(prefix, 'CellAlerts', cellId);

/**
 * True when a topic string is safe to send — non-empty, and free of the `undefined`/`null`
 * segments that a bad interpolation leaves behind.
 *
 * Matches the literal substrings rather than a strict topic grammar on purpose: this is a
 * backstop against interpolation accidents, not a replacement for the ws server's own
 * validation, and it must not start rejecting legitimate topic shapes the server accepts.
 */
export const isSendableTopic = (topic?: string | null): boolean =>
    typeof topic === 'string' && topic.length > 0 && !/(?:undefined|null)/.test(topic);

/**
 * Drop any filter whose `kafkaTopic` shows an interpolation accident, warning once per
 * dropped filter so the offending chart is findable from the browser console.
 *
 * This is the last line of defence at the send sink. Call sites should still build their
 * topics with `buildKafkaTopic` and skip early with a warning naming the signal — reaching
 * this function at all means a call site missed its guard.
 */
export const sanitizeTopicFilters = <T extends { kafkaTopic?: string }>(
    filters: T[],
    context = 'websocket',
): T[] =>
    filters.filter((filter) => {
        if (isSendableTopic(filter?.kafkaTopic)) return true;
        console.warn(
            `[${context}] Dropping live-data filter with an invalid kafkaTopic — an id was ` +
                'missing where the topic was built. Build topics with buildKafkaTopic().',
            { filter },
        );
        return false;
    });
